import { Moment } from 'moment-timezone';
import { AddonSchedule } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import { Option } from 'types/Generics';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Pet } from 'types/Pet';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';
import { DosageQuantity } from 'types/Task';

export const NEXT_AVAILABLE_DATE_WEEKS = 8;

export enum OnlineBookingStep {
    Home = 'home',
    Signup = 'signup',
    Signin = 'signin',
    CreatePet = 'create_pet',
    ServiceCategory = 'service_category',
    PetList = 'pet_list',
    ServiceList = 'service_list',
    AddonList = 'addon_list',
    StaffList = 'staff_availability',
    DatePicker = 'date_picker',
    TimePicker = 'time_picker',
    Instructions = 'instructions',
    Agreements = 'notes_and_agreements',
    Checkout = 'check_out',
    Success = 'success',
    JoinWaitlist = 'join_waitlist',
    WaitlistSuccess = 'waitlist_success',
    Purchase = 'purchase',
    PurchaseCheckout = 'purchase_check_out',
    PurchaseSuccess = 'purchase_success'
}

export type CheckoutView = 'cards_list' | 'add_card';

export interface OnlineBookingPreferences {
    enabled: boolean;
    bookingDateLimits: {
        from: string | null;
        to: string | null;
    };
    allowNewClients: boolean;
    allowSelectStaff: boolean;
    bookingRequestCategories: Partial<Record<MarketplaceServiceCategory, boolean>>;
    promoImageUrl: string;
    pricingOption: OnlineBookingPricingOption;
}

export type TimeLimit = Option<string> & {
    offsetSeconds: number;
};

export const OnlineBookingPricingOptions = {
    StartingAt: 'starting_at',
    Price: 'price'
} as const;

export type OnlineBookingPricingOption =
    (typeof OnlineBookingPricingOptions)[keyof typeof OnlineBookingPricingOptions];

export interface OnlineBookingTime {
    hour: number;
    minute: number;
    label: string;
    staffId?: number;
    datetime?: string;
}

export interface OnlineBookingItemBase {
    uuid: string;
    pet: Pet;
    service?: Service;
    addons: Array<AddonService>;
    // Per Add-On dates and quantity. Absent means the legacy default: once, quantity 1.
    addonSchedules?: Record<number, AddonSchedule>;
    // The stay (boarding) or days (daycare) the Add-On schedules were set for (see getAddonScheduleStayKey).
    addonSchedulesStay?: string;
    businessArea: MarketplaceServiceCategory;
}

export interface OnlineBookingGroomingItem extends OnlineBookingItemBase {
    businessArea: MarketplaceServiceCategory.GROOMING | MarketplaceServiceCategory.DENTAL;
    date?: Moment;
    // The date picker pre-selects a day on mount, so `date` alone doesn't mean the client chose it
    dateSelectedByClient?: boolean;
    staff?: Staff;
    preferredStaff?: Staff;
    time?: OnlineBookingTime;
}

export interface OnlineBookingDaycareDay {
    date: Moment;
    roomUnitId?: number;
}

export interface OnlineBookingDaycareItem extends OnlineBookingItemBase {
    businessArea: MarketplaceServiceCategory.DAYCARE;
    days?: Array<OnlineBookingDaycareDay>;
    dropOffTime?: OnlineBookingTime;
    pickUpTime?: OnlineBookingTime;
    feedings?: Array<Feeding>;
    medications?: Array<Medication>;
}

export interface OnlineBookingBoardingItem extends OnlineBookingItemBase {
    businessArea: MarketplaceServiceCategory.BOARDING;
    startDate?: Moment;
    endDate?: Moment;
    checkInTime?: OnlineBookingTime;
    checkOutTime?: OnlineBookingTime;
    roomUnitId?: number;
    feedings?: Array<Feeding>;
    medications?: Array<Medication>;
}

export type SessionBusinessArea =
    | MarketplaceServiceCategory.WALKING
    | MarketplaceServiceCategory.TRAINING;

export const SESSION_BUSINESS_AREAS: Array<SessionBusinessArea> = [
    MarketplaceServiceCategory.WALKING,
    MarketplaceServiceCategory.TRAINING
];

export const isSessionBusinessArea = (
    businessArea: MarketplaceServiceCategory
): businessArea is SessionBusinessArea =>
    (SESSION_BUSINESS_AREAS as Array<MarketplaceServiceCategory>).includes(businessArea);

export interface SessionStepProps {
    businessArea: SessionBusinessArea;
}

interface OnlineBookingSessionItemBase extends OnlineBookingItemBase {
    businessArea: SessionBusinessArea;
    date?: Moment;
    time?: OnlineBookingTime;
    staff?: Staff;
    preferredStaff?: Staff;
    addressId?: number;
}

export interface OnlineBookingWalkingItem extends OnlineBookingSessionItemBase {
    businessArea: MarketplaceServiceCategory.WALKING;
}

export interface OnlineBookingTrainingItem extends OnlineBookingSessionItemBase {
    businessArea: MarketplaceServiceCategory.TRAINING;
}

export type OnlineBookingSessionItem = OnlineBookingWalkingItem | OnlineBookingTrainingItem;

export interface OnlineBookingEvaluationItem extends OnlineBookingItemBase {
    businessArea: MarketplaceServiceCategory.EVALUATION;
    evaluationBusinessArea: MarketplaceServiceCategory;
    date?: Moment;
    staff?: Staff;
    time?: OnlineBookingTime;
}

export enum MedicationScheduleMode {
    Daily = 'daily',
    SpecificDays = 'specific-days'
}

export interface Feeding {
    momentTypes: Array<number>;
    foodProvisionType: number;
    foodType: number;
    quantity: DosageQuantity;
    brand?: string;
    instructions?: string;
}

export interface Medication {
    name: string;
    methodType: number;
    dosage: DosageQuantity;
    instructions?: string;
    schedule: Array<MedicationSchedule>;
    scheduleMode: MedicationScheduleMode;
}

export interface MedicationSchedule {
    hour: number;
    minute: number;
    date?: Moment;
}

export type OnlineBookingItem =
    | OnlineBookingGroomingItem
    | OnlineBookingDaycareItem
    | OnlineBookingBoardingItem
    | OnlineBookingWalkingItem
    | OnlineBookingTrainingItem
    | OnlineBookingEvaluationItem;

export const isOnlineBookingGroomingItem = (
    item: OnlineBookingItem
): item is OnlineBookingGroomingItem => {
    return [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL].includes(
        item.businessArea
    );
};

export const isOnlineBookingDaycareItem = (
    item: OnlineBookingItem
): item is OnlineBookingDaycareItem => {
    return item.businessArea === MarketplaceServiceCategory.DAYCARE;
};

export const isOnlineBookingBoardingItem = (
    item: OnlineBookingItem
): item is OnlineBookingBoardingItem => {
    return item.businessArea === MarketplaceServiceCategory.BOARDING;
};

export const isOnlineBookingEvaluationItem = (
    item: OnlineBookingItem
): item is OnlineBookingEvaluationItem => {
    return item.businessArea === MarketplaceServiceCategory.EVALUATION;
};

export const isOnlineBookingWalkingItem = (
    item: OnlineBookingItem
): item is OnlineBookingWalkingItem => {
    return item.businessArea === MarketplaceServiceCategory.WALKING;
};

export const isOnlineBookingTrainingItem = (
    item: OnlineBookingItem
): item is OnlineBookingTrainingItem => {
    return item.businessArea === MarketplaceServiceCategory.TRAINING;
};

export const isOnlineBookingSessionItem = (
    item: OnlineBookingItem
): item is OnlineBookingSessionItem => {
    return isSessionBusinessArea(item.businessArea);
};

export const isOnlineBookingSessionItemOf =
    (businessArea: SessionBusinessArea) =>
    (item: OnlineBookingItem): item is OnlineBookingSessionItem =>
        item.businessArea === businessArea;

export const isOnlineBookingItemIncomplete = (item: OnlineBookingItem) => {
    if (
        isOnlineBookingGroomingItem(item) ||
        isOnlineBookingEvaluationItem(item) ||
        isOnlineBookingSessionItem(item)
    ) {
        return !item.service || !item.time || !item.date;
    }

    if (isOnlineBookingDaycareItem(item)) {
        return (
            !item.service ||
            !item.days?.length ||
            !item.dropOffTime ||
            !item.pickUpTime ||
            item.days.some(day => day.roomUnitId === undefined)
        );
    }

    if (isOnlineBookingBoardingItem(item)) {
        return (
            !item.service ||
            !item.startDate ||
            !item.endDate ||
            !item.checkInTime ||
            !item.checkOutTime
        );
    }

    return false;
};

const getOnlineBookingItemCategory = (item: OnlineBookingItem): MarketplaceServiceCategory => {
    const category = isOnlineBookingEvaluationItem(item)
        ? item.evaluationBusinessArea
        : item.businessArea;

    return category === MarketplaceServiceCategory.DENTAL
        ? MarketplaceServiceCategory.GROOMING
        : category;
};

export const countCompletedOnlineBookingCategories = (items: Array<OnlineBookingItem>) => {
    const itemsByCategory = new Map<MarketplaceServiceCategory, Array<OnlineBookingItem>>();

    items.forEach(item => {
        const category = getOnlineBookingItemCategory(item);

        itemsByCategory.set(category, [...(itemsByCategory.get(category) || []), item]);
    });

    return Array.from(itemsByCategory.values()).filter(
        categoryItems => !categoryItems.some(isOnlineBookingItemIncomplete)
    ).length;
};

export const isOnlineBookingItemEmpty = (item: OnlineBookingItem) => {
    if (item.addons?.length) return false;

    if (
        isOnlineBookingGroomingItem(item) ||
        isOnlineBookingEvaluationItem(item) ||
        isOnlineBookingSessionItem(item)
    ) {
        return !item.service && !item.time && !item.date;
    }

    if (isOnlineBookingDaycareItem(item)) {
        return !item.service && !item.days?.length && !item.dropOffTime && !item.pickUpTime;
    }

    if (isOnlineBookingBoardingItem(item)) {
        return (
            !item.service &&
            !item.startDate &&
            !item.endDate &&
            !item.checkInTime &&
            !item.checkOutTime
        );
    }

    return false;
};
